import { HttpErrorResponse } from '@angular/common/http';
import { inject } from '@angular/core';
import { tapResponse } from '@ngrx/operators';
import { patchState, signalStore, withHooks, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { Store } from '@ngrx/store';
import { combineLatest, pairwise, pipe, startWith, switchMap, tap } from 'rxjs';
import { filter } from 'rxjs/operators';
import { ActivityHierarchyResponse } from '../../api/data-definitions/models/api/activity-hierarchy-response';
import { ListActivityHierarchiesResponse } from '../../api/data-definitions/models/api/list-activity-hierarchies-response';
import { ActivityHierarchiesService } from '../../api/data-definitions/services';
import { injectTranslate } from '../../functions/inject-functions/inject-translate';
import { injectUntilDestroyed } from '../../functions/inject-functions/inject-until-destroyed';
import { UI_STRINGS } from '../../functions/language/uistrings-tolgee';
import { InformationDialogComponent } from '../../modules/shared/ui-kit/dialog/components/information-dialog/information-dialog.component';
import { DialogService } from '../../modules/shared/ui-kit/dialog/dialog.service';
import { BoardFacadeService } from '../../services/store/board/board-facade.service';
import { selectRouteBoardId } from '../router/router.selectors';

type ActivityHierarchiesState = {
  error: Error | undefined;
  isLoading: boolean;
  listed: boolean;
  dashBoardActivityHierarchy: ActivityHierarchyResponse | undefined;
  activityHierarchies: Record<string, ActivityHierarchyResponse>; // Map-like object
};

const initialState: ActivityHierarchiesState = {
  error: undefined,
  isLoading: false,
  listed: false,
  dashBoardActivityHierarchy: undefined,
  activityHierarchies: {},
};

const injectHandleError = () => {
  const translate = injectTranslate();
  const dialogService = inject(DialogService);
  return (error: Error | HttpErrorResponse) => {
    translate
      .toObservable(
        {
          title: UI_STRINGS.ACTIVITY_INVENTORY.ERROR_LOADING_TITLE,
          text: UI_STRINGS.ACTIVITY_INVENTORY.ERROR_LOADING_DESCRIPTION,
          closeLabel: UI_STRINGS.GLOBAL.CLOSE,
        },
        { completeAfterFirstTranslation: true }
      )
      .subscribe(({ title, text, closeLabel }) => {
        const details =
          'error' in error && error.error.message ? error.message + '\n\n' + error.error.message : error.message;

        dialogService.open(InformationDialogComponent, {
          data: { type: 'error', title, text, details, closeLabel },
        });
      });
  };
};

export const ActivityHierarchiesStore = signalStore(
  { providedIn: 'root' },
  withState(initialState),
  withMethods((store) => {
    const activityHierarchyService = inject(ActivityHierarchiesService);
    const boardFacadeService = inject(BoardFacadeService);
    const errorHandler = injectHandleError();
    return {
      loadDashBoardActivityHierarchy: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(() =>
            combineLatest([
              boardFacadeService.getBoardProperty$(['boardSettings', 'activityHierarchy']).pipe(filter(Boolean)),
              boardFacadeService.boardLanguage$.pipe(filter(Boolean)),
            ])
          ),
          switchMap(([dashboardHierarchyId, language]) =>
            activityHierarchyService
              .getActivityHierarchy({ id: dashboardHierarchyId, 'Accept-Language': language })
              .pipe(
                tapResponse({
                  next: (hierarchy) => {
                    patchState(store, (state) => {
                      const updatedActivityHierarchies = {
                        ...state.activityHierarchies,
                        [hierarchy.id]: hierarchy, // Add or update the hierarchy in the state map
                      };
                      return {
                        ...state,
                        dashBoardActivityHierarchy: hierarchy,
                        activityHierarchies: updatedActivityHierarchies,
                        isLoading: false,
                        error: undefined,
                      };
                    });
                  },
                  error: (error: Error | HttpErrorResponse) => {
                    patchState(store, { isLoading: false, error });
                    errorHandler(error);
                  },
                })
              )
          )
        )
      ),
      loadActivityHierarchy: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isLoading: true })), // Set loading state
          switchMap((id) =>
            boardFacadeService.boardLanguage$.pipe(
              filter(Boolean), // Ensure language is not null/undefined
              switchMap((language) =>
                activityHierarchyService
                  .getActivityHierarchy({ 'Accept-Language': language, id }) // Fetch hierarchy by ID
                  .pipe(
                    tapResponse({
                      next: (hierarchy) => {
                        // Update the state with the hierarchy
                        patchState(store, (state) => {
                          const updatedActivityHierarchies = {
                            ...state.activityHierarchies,
                            [hierarchy.id]: hierarchy, // Add or update the hierarchy in the state map
                          };
                          return {
                            ...state,
                            activityHierarchies: updatedActivityHierarchies,
                            isLoading: false,
                            error: undefined,
                          };
                        });
                      },
                      error: (error: Error | HttpErrorResponse) => {
                        console.error('Error loading activity hierarchy:', error); // Log the error
                        patchState(store, { isLoading: false, error });
                        errorHandler(error);
                      },
                    })
                  )
              )
            )
          )
        )
      ),
      loadActivityHierarchyByName: rxMethod<string>(
        pipe(
          tap(() => patchState(store, { isLoading: true })), // Set loading state
          switchMap((name) =>
            boardFacadeService.boardLanguage$.pipe(
              filter(Boolean), // Ensure language is not null/undefined
              switchMap((language) =>
                activityHierarchyService
                  .getActivityHierarchyByName({ 'Accept-Language': language, name }) // Fetch hierarchy by Name
                  .pipe(
                    tapResponse({
                      next: (hierarchy) => {
                        // Update the state with the hierarchy
                        patchState(store, (state) => {
                          const updatedActivityHierarchies = {
                            ...state.activityHierarchies,
                            [hierarchy.id]: hierarchy, // Add or update the hierarchy in the state map
                          };
                          return {
                            ...state,
                            activityHierarchies: updatedActivityHierarchies,
                            isLoading: false,
                            error: undefined,
                          };
                        });
                      },
                      error: (error: Error | HttpErrorResponse) => {
                        console.error('Error loading activity hierarchy:', error); // Log the error
                        patchState(store, { isLoading: false, error });
                        errorHandler(error);
                      },
                    })
                  )
              )
            )
          )
        )
      ),
      loadActivityHierarchies: rxMethod<void>(
        pipe(
          tap(() => patchState(store, { isLoading: true })),
          switchMap(() =>
            boardFacadeService.boardLanguage$.pipe(
              filter(Boolean),
              switchMap((language) =>
                activityHierarchyService.listActivityHierarchies({ 'Accept-Language': language }).pipe(
                  tapResponse({
                    next: (listResponse: ListActivityHierarchiesResponse) => {
                      patchState(store, (state) => {
                        // loop through new hierarchies and add them to the state if not already loaded
                        const updatedActivityHierarchies = (listResponse.activityHierarchies ?? []).reduce(
                          (hierarchies, hierarchy) => {
                            if (!hierarchies[hierarchy.id]) {
                              hierarchies[hierarchy.id] = hierarchy;
                            }
                            return hierarchies;
                          },
                          structuredClone(state.activityHierarchies ?? {})
                        );
                        return {
                          ...state,
                          activityHierarchies: updatedActivityHierarchies,
                          isLoading: false,
                          listed: true,
                          error: undefined,
                        };
                      });
                    },
                    error: (error: Error | HttpErrorResponse) => {
                      patchState(store, { isLoading: false, error });
                      errorHandler(error);
                    },
                  })
                )
              )
            )
          )
        )
      ),
    };
  }),
  withHooks({
    onInit: (store) => {
      const untilDestroyed = injectUntilDestroyed();
      inject(Store)
        .select(selectRouteBoardId)
        .pipe(
          untilDestroyed(),
          startWith(undefined),
          pairwise(),
          filter(([prev, curr]) => prev !== undefined && prev !== curr)
        )
        .subscribe(() => {
          // resetting the state on board change
          patchState(store, initialState);
        });
    },
  })
);
